import { Injectable, Inject, Optional, PLATFORM_ID, REQUEST } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  DEFAULT_LANGUAGE_CODE,
  DEFAULT_LOGO_PATH,
  DEFAULT_SITE_ORIGIN,
  buildAbsoluteUrl,
  buildAlternateLanguageLinks,
  buildLocalizedPagePath,
  getLanguageLocale,
  getNormalizedLanguageCodes,
  getOgImagePath,
  getRequestUrl,
  normalizeLanguageCode,
} from './site-config';

const SITE_NAME = 'GLT Design';

interface PageSeoEntry {
  defaultDescription: string;
}

const PAGE_SEO_MAP: Record<string, PageSeoEntry> = {
  index: {
    defaultDescription: 'GLT Design - Professional software development, custom web applications, ' +
      'mobile apps, and IT solutions for businesses across multiple industries.',
  },
  about: {
    defaultDescription: 'About GLT Design - Meet our team and discover our approach to delivering ' +
      'high-quality custom software development and IT consulting services.',
  },
  products: {
    defaultDescription: 'GLT Design Products - Explore our software products and solutions designed ' +
      'for business automation, management, and productivity.',
  },
  contacts: {
    defaultDescription: 'Contact GLT Design - Reach out to discuss your software development, ' +
      'IT consulting, or business automation project needs.',
  },
};

@Injectable({ providedIn: 'root' })
export class SeoService {

  constructor(
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request: Request | null
  ) {}

  setPageSeo(pageKey: string, langData: any, languageCode: string, languageCodes: string[] = []): void {
    const normalizedPageKey = PAGE_SEO_MAP[pageKey] ? pageKey : 'index';
    const seoEntry = PAGE_SEO_MAP[normalizedPageKey] || PAGE_SEO_MAP['index'];
    const baseOrigin = this.getBaseOrigin();
    const currentLanguageCode = normalizeLanguageCode(languageCode);
    const availableLanguageCodes = getNormalizedLanguageCodes(languageCodes);
    const siteTitleSuffix = langData?.defdata?.title || SITE_NAME;
    const pageLabel = langData?.pages?.[normalizedPageKey]?.title || SITE_NAME;
    const fullTitle = `${pageLabel} :: ${siteTitleSuffix}`;
    const description = langData?.pages?.[normalizedPageKey]?.seo_description || seoEntry.defaultDescription;
    const canonicalUrl = buildAbsoluteUrl(baseOrigin, buildLocalizedPagePath(normalizedPageKey, currentLanguageCode));
    const imageUrl = buildAbsoluteUrl(baseOrigin, getOgImagePath(currentLanguageCode));
    const currentLocale = getLanguageLocale(currentLanguageCode);

    if (!availableLanguageCodes.includes(currentLanguageCode)) {
      availableLanguageCodes.unshift(currentLanguageCode);
    }

    this.titleService.setTitle(fullTitle);
    this.updateCanonical(canonicalUrl);
    this.updateHtmlLanguage(currentLanguageCode);
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
    this.metaService.updateTag({ name: 'language', content: currentLanguageCode });
    this.metaService.updateTag({ property: 'og:title', content: fullTitle });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
    this.metaService.updateTag({ property: 'og:image', content: imageUrl });
    this.metaService.updateTag({ property: 'og:locale', content: currentLocale });
    this.metaService.updateTag({ name: 'twitter:title', content: fullTitle });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
    this.metaService.updateTag({ name: 'twitter:image', content: imageUrl });
    this.updateAlternateLanguageLinks(baseOrigin, normalizedPageKey, availableLanguageCodes);
    this.updateOgLocaleAlternates(currentLanguageCode, availableLanguageCodes);
    this.updateStructuredData(baseOrigin, canonicalUrl, currentLanguageCode, fullTitle, description, availableLanguageCodes);
  }

  private updateCanonical(url: string): void {
    let canonicalLink = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null | undefined;
    const headElement = this.document.head;

    if (null == canonicalLink) {
      canonicalLink = this.document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      if (null !== headElement) {
        headElement.appendChild(canonicalLink);
      }
    }
    canonicalLink.setAttribute('href', url);
  }

  private getBaseOrigin(): string {
    const requestUrl = getRequestUrl(this.request);
    let result = DEFAULT_SITE_ORIGIN;

    if (isPlatformBrowser(this.platformId)) {
      result = window.location.origin;
    } else if (null !== requestUrl) {
      result = requestUrl.origin;
    }

    return result;
  }

  private updateHtmlLanguage(languageCode: string): void {
    this.document.documentElement.setAttribute('lang', languageCode);
  }

  private updateAlternateLanguageLinks(baseOrigin: string, pageKey: string, languageCodes: string[]): void {
    const headElement = this.document.head;
    const alternateLinks = this.document.querySelectorAll('link[data-glt-hreflang="true"]');

    alternateLinks.forEach((element) => element.remove());

    if (null === headElement) {
      return;
    }

    buildAlternateLanguageLinks(baseOrigin, pageKey, languageCodes).forEach((alternateLink) => {
      const linkElement = this.document.createElement('link');

      linkElement.setAttribute('rel', 'alternate');
      linkElement.setAttribute('hreflang', alternateLink.hrefLang);
      linkElement.setAttribute('href', alternateLink.url);
      linkElement.setAttribute('data-glt-hreflang', 'true');
      headElement.appendChild(linkElement);
    });

    const defaultLinkElement = this.document.createElement('link');

    defaultLinkElement.setAttribute('rel', 'alternate');
    defaultLinkElement.setAttribute('hreflang', 'x-default');
    defaultLinkElement.setAttribute('href', buildAbsoluteUrl(baseOrigin, buildLocalizedPagePath(pageKey, DEFAULT_LANGUAGE_CODE)));
    defaultLinkElement.setAttribute('data-glt-hreflang', 'true');
    headElement.appendChild(defaultLinkElement);
  }

  private updateOgLocaleAlternates(currentLanguageCode: string, languageCodes: string[]): void {
    const headElement = this.document.head;
    const alternateLocaleTags = this.document.querySelectorAll('meta[data-glt-og-locale-alternate="true"]');

    alternateLocaleTags.forEach((element) => element.remove());

    if (null === headElement) {
      return;
    }

    getNormalizedLanguageCodes(languageCodes)
      .filter((languageCode) => languageCode !== currentLanguageCode)
      .forEach((languageCode) => {
        const metaElement = this.document.createElement('meta');

        metaElement.setAttribute('property', 'og:locale:alternate');
        metaElement.setAttribute('content', getLanguageLocale(languageCode));
        metaElement.setAttribute('data-glt-og-locale-alternate', 'true');
        headElement.appendChild(metaElement);
      });
  }

  private updateStructuredData(
    baseOrigin: string,
    canonicalUrl: string,
    languageCode: string,
    fullTitle: string,
    description: string,
    languageCodes: string[]
  ): void {
    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': buildAbsoluteUrl(baseOrigin, '/#organization'),
          name: SITE_NAME,
          url: baseOrigin,
          logo: buildAbsoluteUrl(baseOrigin, DEFAULT_LOGO_PATH),
          sameAs: [],
        },
        {
          '@type': 'WebSite',
          '@id': buildAbsoluteUrl(baseOrigin, '/#website'),
          url: baseOrigin,
          name: SITE_NAME,
          inLanguage: getNormalizedLanguageCodes(languageCodes),
          publisher: {
            '@id': buildAbsoluteUrl(baseOrigin, '/#organization'),
          },
        },
        {
          '@type': 'WebPage',
          '@id': canonicalUrl + '#webpage',
          url: canonicalUrl,
          name: fullTitle,
          description,
          inLanguage: languageCode,
          isPartOf: {
            '@id': buildAbsoluteUrl(baseOrigin, '/#website'),
          },
          about: {
            '@id': buildAbsoluteUrl(baseOrigin, '/#organization'),
          },
        },
      ],
    };
    let jsonLdScript = this.document.querySelector('script[data-glt-jsonld="website"]') as HTMLScriptElement | null | undefined;
    const headElement = this.document.head;

    if (null == jsonLdScript) {
      jsonLdScript = this.document.createElement('script') as HTMLScriptElement;
      jsonLdScript.setAttribute('type', 'application/ld+json');
      jsonLdScript.setAttribute('data-glt-jsonld', 'website');
      if (null !== headElement) {
        headElement.appendChild(jsonLdScript);
      }
    }

    jsonLdScript.textContent = JSON.stringify(structuredData);
  }
}
